import { useCallback, useEffect } from 'react';

import { useAppDispatch } from '@/configuration/setup/hooks';
import { requestComposerFocus } from '@/store/composerFocus/composerFocusSlice';
import { hasOpenDialog } from '@/utils/hasOpenDialog/hasOpenDialog';

import type { TUseDocumentViewerFocusParams } from './types';

/**
 * Moves keyboard focus into the Document Viewer when it opens and hands it back when it closes.
 *
 * The move in is guarded since a modal dialog that owns focus (the session-expired dialog, release notes) keeps
 * it, because the Viewer opening behind it must not pull the user out of its focus trap. In overlay mode
 * the Viewer is itself a dialog, so it is excluded from that check.
 */
export const useDocumentViewerFocus = ({
  containerRef,
  headingRef,
  sourceElementId,
}: TUseDocumentViewerFocusParams) => {
  const dispatch = useAppDispatch();

  // INFO: Keeps the focus on the viewer when a document source is changed after the viewer is already open
  useEffect(() => {
    if (hasOpenDialog(containerRef.current)) return;

    headingRef.current?.focus();
  }, [containerRef, headingRef]);

  return useCallback(() => {
    // INFO: Waits for re-render and returns the focus back to the element that triggered the viewer opening after it is closed.
    window.requestAnimationFrame(() => {
      const openingSource = sourceElementId ? document.getElementById(sourceElementId) : null;
      if (openingSource) {
        openingSource.focus();
        return;
      }

      // INFO: Returns the focus back to the input when the viewer is no long live.
      dispatch(requestComposerFocus());
    });
  }, [dispatch, sourceElementId]);
};
